import { useEffect, useState } from 'react'

// When the viewport is narrower than this and the UA is NOT a mobile device,
// we still render the mobile shell so the board remains visible — but we keep
// `isMobile` (UA) false so phone-only keyboard hacks (Timer/PlayerInfo bottom
// shifts, chat fullscreen, etc.) do NOT fire.
export const COMPACT_BREAKPOINT = 700

/**
 * `isMobile` — User-Agent based. Drives phone-only behaviour:
 *   - chat fullscreen + auto-blur
 *   - Timer / PlayerInfo `style.bottom` adjustments for the soft keyboard
 *   - fullscreen entry
 *
 * `isCompact` — UA mobile OR window width < `COMPACT_BREAKPOINT`. Controls
 * which Game shell is rendered (mobile vs desktop). Narrow desktop windows
 * therefore look "just like mobile, without the keyboard adjustments".
 *
 * Side-effects:
 *   - `body.mobile-layout` / `body.desktop-layout` is toggled from `isCompact`
 *     so the 500px `#root` cap also applies to narrow desktop windows.
 */
export const useIsMobile = () => {
    const computeMobile = () => {
        if (typeof window === 'undefined') return false
        if (typeof window.mobileCheck === 'function') return Boolean(window.mobileCheck())
        return /android|iphone|ipad|ipod|mobile|tablet/i.test(navigator.userAgent || '')
    }
    const computeCompact = (uaMobile) => {
        if (uaMobile) return true
        if (typeof window === 'undefined') return false
        return window.innerWidth < COMPACT_BREAKPOINT
    }

    const [isMobile, setIsMobile] = useState(computeMobile)
    const [isCompact, setIsCompact] = useState(() => computeCompact(computeMobile()))

    useEffect(() => {
        const onChange = () => {
            const ua = computeMobile()
            setIsMobile(prev => (prev === ua ? prev : ua))
            const cmp = computeCompact(ua)
            setIsCompact(prev => (prev === cmp ? prev : cmp))
        }
        window.addEventListener('orientationchange', onChange)
        window.addEventListener('resize', onChange)
        return () => {
            window.removeEventListener('orientationchange', onChange)
            window.removeEventListener('resize', onChange)
        }
    }, [])

    useEffect(() => {
        const mob = 'mobile-layout'
        const desk = 'desktop-layout'
        if (isCompact) {
            document.body.classList.add(mob)
            document.body.classList.remove(desk)
        } else {
            document.body.classList.remove(mob)
            document.body.classList.add(desk)
        }
    }, [isCompact])

    return { isMobile, isCompact }
}
